import type { MembershipSummary, TenantMemberProfile, TenantMemberUpdate } from '@livechat/shared';
import { type ChangeEvent, type FormEvent, useEffect, useRef, useState } from 'react';

import {
	changePassword,
	completeMemberInvitation,
	deleteMember,
	getMemberProfile,
	updateMember,
	updateMemberProfile,
} from '../api.js';
import { avatarPresets } from '../avatar-presets.js';
import type { ChatToast } from '../chat-notifications.js';
import {
	notificationSoundChoice,
	notificationSoundPreference,
	notificationTestToasts,
	playNotificationSound,
	requestSystemNotificationPermission,
	showSystemNotification,
	visualNotificationMethod,
} from '../notification-alerts.js';
import { MemberAvatar } from './MemberAvatar.js';
import { NotificationToastRegion } from './NotificationToastRegion.js';
import { TenantSettingsFormLayout } from './TenantSettingsFormLayout.js';

async function prepareAvatar(file: File): Promise<string> {
	if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
		throw new Error('Choose a PNG, JPEG or WebP image');
	}
	if (file.size > 5_000_000) throw new Error('Avatar images must be smaller than 5 MB');
	const source = URL.createObjectURL(file);
	try {
		const image = await new Promise<HTMLImageElement>((resolve, reject) => {
			const element = new Image();
			element.onload = () => resolve(element);
			element.onerror = () => reject(new Error('Could not read that image'));
			element.src = source;
		});
		const canvas = document.createElement('canvas');
		canvas.width = 256;
		canvas.height = 256;
		const context = canvas.getContext('2d');
		if (!context) throw new Error('Image processing is unavailable');
		const side = Math.min(image.naturalWidth, image.naturalHeight);
		const x = (image.naturalWidth - side) / 2;
		const y = (image.naturalHeight - side) / 2;
		context.drawImage(image, x, y, side, side, 0, 0, 256, 256);
		return canvas.toDataURL('image/webp', 0.82);
	} finally {
		URL.revokeObjectURL(source);
	}
}

export function TenantSettings({
	currentUserId,
	membership,
	onProfileChanged,
	onMemberDeleted,
	targetUserId,
}: {
	currentUserId: string;
	membership: MembershipSummary;
	onProfileChanged: (profile: TenantMemberProfile) => void;
	onMemberDeleted: () => void;
	targetUserId?: string;
}) {
	const userId = targetUserId ?? currentUserId;
	const isSelf = userId === currentUserId;
	const [profile, setProfile] = useState<TenantMemberProfile>();
	const [busy, setBusy] = useState(false);
	const [message, setMessage] = useState<string>();
	const [error, setError] = useState<string>();
	const [confirmDelete, setConfirmDelete] = useState(false);
	const [currentPassword, setCurrentPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [passwordBusy, setPasswordBusy] = useState(false);
	const [passwordError, setPasswordError] = useState<string>();
	const [testToasts, setTestToasts] = useState<ChatToast[]>([]);
	const [notificationTestResult, setNotificationTestResult] = useState<{
		kind: 'error' | 'success';
		message: string;
	}>();
	const testToastTimerRef = useRef<number | undefined>(undefined);

	useEffect(() => {
		setError(undefined);
		setMessage(undefined);
		setCurrentPassword('');
		setNewPassword('');
		setConfirmPassword('');
		setPasswordError(undefined);
		setProfile(undefined);
		void getMemberProfile(membership.tenantId, userId)
			.then(setProfile)
			.catch((caught: unknown) =>
				setError(caught instanceof Error ? caught.message : 'Could not load user settings'),
			);
	}, [membership.tenantId, userId]);

	useEffect(
		() => () => {
			window.clearTimeout(testToastTimerRef.current);
		},
		[],
	);

	function changeProfile<K extends keyof TenantMemberProfile>(
		key: K,
		value: TenantMemberProfile[K],
	): void {
		setProfile((current) => (current ? { ...current, [key]: value } : current));
	}

	function changeNotificationSound(value: string): void {
		const preference = notificationSoundPreference(
			value as Parameters<typeof notificationSoundPreference>[0],
		);
		setProfile((current) => {
			if (!current) return current;
			return {
				...current,
				...(preference.sound ? { notificationSound: preference.sound } : {}),
				soundNotificationsEnabled: preference.enabled,
			};
		});
	}

	async function chooseAvatar(event: ChangeEvent<HTMLInputElement>): Promise<void> {
		const input = event.currentTarget;
		const files = Array.from(input.files ?? []);
		if (!files.length || !profile) return;
		setError(undefined);
		try {
			const dataUrls = await Promise.all(files.map(prepareAvatar));
			const avatarDataUrls = [...profile.avatarDataUrls];
			for (const dataUrl of dataUrls) {
				if (!avatarDataUrls.includes(dataUrl)) avatarDataUrls.push(dataUrl);
			}
			if (avatarDataUrls.length > 20) {
				throw new Error('You can store up to 20 custom avatars');
			}
			setProfile((current) =>
				current
					? {
							...current,
							avatarDataUrl: dataUrls.at(-1),
							avatarDataUrls,
							avatarEnabled: true,
							avatarKind: 'uploaded',
						}
					: current,
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not prepare that avatar');
		} finally {
			input.value = '';
		}
	}

	function deleteCustomAvatar(dataUrl: string): void {
		setProfile((current) => {
			if (!current) return current;
			const deletingSelected =
				current.avatarKind === 'uploaded' && current.avatarDataUrl === dataUrl;
			return {
				...current,
				...(deletingSelected
					? { avatarDataUrl: undefined, avatarKind: 'preset' as const }
					: {}),
				avatarDataUrls: current.avatarDataUrls.filter((avatar) => avatar !== dataUrl),
			};
		});
	}

	async function toggleSystemNotifications(enabled: boolean): Promise<void> {
		if (!enabled) {
			changeProfile('browserNotificationsEnabled', false);
			return;
		}
		const granted = await requestSystemNotificationPermission();
		if (granted) {
			changeProfile('browserNotificationsEnabled', true);
			setError(undefined);
		} else {
			changeProfile('browserNotificationsEnabled', false);
			setError('Allow system notifications in your browser before enabling this option.');
		}
	}

	function dismissTestToast(id: string): void {
		setTestToasts((current) => current.filter((toast) => toast.id !== id));
	}

	async function testSelectedNotifications(): Promise<void> {
		if (!profile) return;
		setNotificationTestResult(undefined);
		const toasts = notificationTestToasts(profile);
		if (!toasts.length) {
			setNotificationTestResult({
				kind: 'error',
				message: 'Enable new-customer or new-message notifications before testing.',
			});
			return;
		}

		const method = visualNotificationMethod(profile.browserNotificationsEnabled);
		if (method === 'browser') {
			setTestToasts(toasts);
			window.clearTimeout(testToastTimerRef.current);
			testToastTimerRef.current = window.setTimeout(() => setTestToasts([]), 8_000);
		} else {
			window.clearTimeout(testToastTimerRef.current);
			setTestToasts([]);
		}

		const channels = [`${method} alerts`];
		if (profile.soundNotificationsEnabled) {
			await playNotificationSound(profile.notificationSound);
			channels.push('sound');
		}
		if (method === 'system') {
			const results = await Promise.all(
				toasts.map((toast) => showSystemNotification(toast, () => undefined)),
			);
			const failed = results.find((result) => result.status !== 'shown');
			if (failed) {
				const failureMessage =
					failed.status === 'unsupported'
						? 'This browser does not support system notifications.'
						: failed.status === 'permission-required'
							? 'System notification permission is not enabled for this website.'
							: 'The browser could not hand the notification to your operating system.';
				setNotificationTestResult({
					kind: 'error',
					message: `${failureMessage}${
						profile.soundNotificationsEnabled ? ' The sound test still ran.' : ''
					}`,
				});
				return;
			}
		}

		const channelList =
			channels.length === 1
				? channels[0]
				: `${channels.slice(0, -1).join(', ')} and ${channels.at(-1)}`;
		setNotificationTestResult({
			kind: 'success',
			message: `Test sent using ${channelList}.${
				method === 'system'
					? ' The browser accepted the system alert; if it is not visible, allow Chrome notifications in Windows Settings.'
					: ''
			}`,
		});
	}

	async function saveUserSettings(event: FormEvent<HTMLFormElement>): Promise<void> {
		event.preventDefault();
		if (!profile) return;
		setBusy(true);
		setError(undefined);
		setMessage(undefined);
		try {
			let updated = await updateMemberProfile(membership.tenantId, userId, {
				...(isSelf && profile.memberType === 'human' && profile.aiCopilotAvailable
					? { aiCopilotEnabled: profile.aiCopilotEnabled }
					: {}),
				...(profile.canManageAiInstructions
					? { aiInstructions: profile.aiInstructions }
					: {}),
				...(profile.canManageAutoOffline
					? { autoOfflineMinutes: profile.autoOfflineMinutes }
					: {}),
				avatarDataUrl: profile.avatarDataUrl ?? null,
				avatarDataUrls: profile.avatarDataUrls,
				avatarEnabled: profile.avatarEnabled,
				avatarKind: profile.avatarKind,
				avatarPreset: profile.avatarPreset,
				displayName: profile.displayName,
				...(profile.canManageCapacity
					? { maxSimultaneousChats: profile.maxSimultaneousChats }
					: {}),
				...(profile.messageEnhanceAvailable
					? { messageEnhanceLevel: profile.messageEnhanceLevel }
					: {}),
				selectedPresence: profile.selectedPresence,
				...(isSelf
					? {
							browserNotificationsEnabled: profile.browserNotificationsEnabled,
							newChatNotificationsEnabled: profile.newChatNotificationsEnabled,
							newMessageNotificationsEnabled: profile.newMessageNotificationsEnabled,
							notificationSound: profile.notificationSound,
							soundNotificationsEnabled: profile.soundNotificationsEnabled,
						}
					: {}),
			});
			if (profile.canManageAccount && profile.role !== 'owner') {
				const account = await updateMember(membership.tenantId, userId, {
					role: profile.role as TenantMemberUpdate['role'],
					status: profile.status,
				});
				updated = { ...updated, role: account.role, status: account.status };
			}
			setProfile(updated);
			if (isSelf) onProfileChanged(updated);
			setMessage(
				profile.memberType === 'ai_agent'
					? 'AI agent Draft saved. Publish it under Settings → AI when it is ready.'
					: 'User settings saved.',
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not save user settings');
		} finally {
			setBusy(false);
		}
	}

	async function removeMember(): Promise<void> {
		if (!profile) return;
		setBusy(true);
		setError(undefined);
		try {
			await deleteMember(membership.tenantId, userId);
			setConfirmDelete(false);
			onMemberDeleted();
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not delete the user');
		} finally {
			setBusy(false);
		}
	}

	async function savePassword(): Promise<void> {
		if (!profile) return;
		setPasswordError(undefined);
		setMessage(undefined);
		if (isSelf && !currentPassword) {
			setPasswordError('Enter your current password');
			return;
		}
		if (newPassword !== confirmPassword) {
			setPasswordError('The new passwords do not match');
			return;
		}
		if (newPassword.length < 12) {
			setPasswordError('Use at least 12 characters');
			return;
		}
		setPasswordBusy(true);
		try {
			if (isSelf) {
				await changePassword({ currentPassword, password: newPassword });
				setMessage('Password changed. Other signed-in sessions have been logged out.');
			} else {
				const updated = await completeMemberInvitation(
					membership.tenantId,
					userId,
					newPassword,
				);
				setProfile(updated);
				setMessage('Password set and invitation completed. The user can now sign in.');
			}
			setCurrentPassword('');
			setNewPassword('');
			setConfirmPassword('');
		} catch (caught) {
			setPasswordError(
				caught instanceof Error ? caught.message : 'Could not update the password',
			);
		} finally {
			setPasswordBusy(false);
		}
	}

	return (
		<div className="settings-page">
			{error && <div className="alert error">{error}</div>}
			{message && <div className="alert success">{message}</div>}

			<div className="settings-section-heading">
				<h4>
					{profile?.memberType === 'ai_agent' ? 'AI Agent settings' : 'User settings'}
				</h4>
				<p>
					{isSelf
						? 'Your profile, status and chat capacity for this company.'
						: `Editing ${profile?.displayName ?? 'team member'}.`}
				</p>
			</div>
			{profile && (
				<TenantSettingsFormLayout onSubmit={(event) => void saveUserSettings(event)}>
					<section className="settings-card user-profile-panel">
						<div className="profile-preview">
							<MemberAvatar
								dataUrl={
									profile.avatarKind === 'uploaded'
										? profile.avatarDataUrl
										: undefined
								}
								displayName={profile.displayName}
								enabled
								preset={profile.avatarPreset}
								size="large"
								tooltip={false}
							/>
							<div>
								<strong>{profile.displayName}</strong>
								{profile.email && <span>{profile.email}</span>}
							</div>
						</div>
						<div className="settings-grid settings-grid-four">
							<label>
								<span>Display name</span>
								<input
									maxLength={160}
									onChange={(event) =>
										changeProfile('displayName', event.currentTarget.value)
									}
									required
									value={profile.displayName}
								/>
							</label>
							<label>
								<span>Status</span>
								<select
									onChange={(event) =>
										changeProfile(
											'selectedPresence',
											event.currentTarget
												.value as TenantMemberProfile['selectedPresence'],
										)
									}
									value={profile.selectedPresence}
								>
									<option value="available">Available</option>
									<option value="busy">Busy</option>
									<option value="offline">Offline</option>
								</select>
							</label>
							<label>
								<span>Simultaneous chats</span>
								<input
									disabled={!profile.canManageCapacity}
									max={50}
									min={1}
									onChange={(event) =>
										changeProfile(
											'maxSimultaneousChats',
											Number(event.currentTarget.value),
										)
									}
									type="number"
									value={profile.maxSimultaneousChats}
								/>
								<small>
									{profile.activeChatCount} active now. At the limit, status
									becomes Busy automatically.
								</small>
							</label>
							{profile.memberType === 'human' && (
								<label>
									<span>Automatically go offline</span>
									<input
										disabled={!profile.canManageAutoOffline}
										max={1440}
										min={0}
										onChange={(event) =>
											changeProfile(
												'autoOfflineMinutes',
												Number(event.currentTarget.value),
											)
										}
										type="number"
										value={profile.autoOfflineMinutes}
									/>
									<small>
										Minutes without activity before this user is set to Offline.
										Use 0 to disable.
										{!profile.canManageAutoOffline &&
											' Only supervisors or higher can change this setting.'}
									</small>
								</label>
							)}
						</div>

						<div className="avatar-settings">
							<label className="switch-row compact-switch">
								<div className="settings-option-copy">
									<span className="settings-option-title">
										Show avatar in chat
									</span>
									<span className="settings-option-description">
										Display it beside your messages in the visitor chat.
									</span>
								</div>
								<input
									checked={profile.avatarEnabled}
									onChange={(event) =>
										changeProfile('avatarEnabled', event.currentTarget.checked)
									}
									type="checkbox"
								/>
							</label>
							<div className="avatar-choice-row">
								<div>
									<span className="avatar-option-title">Avatars</span>
									<div className="avatar-presets">
										{avatarPresets.map((preset) => (
											<button
												aria-label={`Use ${preset.label} avatar`}
												className={`avatar-choice-button ${
													profile.avatarKind === 'preset' &&
													profile.avatarPreset === preset.id
														? 'selected'
														: ''
												}`}
												key={preset.id}
												onClick={() => {
													changeProfile('avatarKind', 'preset');
													changeProfile('avatarPreset', preset.id);
													changeProfile('avatarDataUrl', undefined);
													changeProfile('avatarEnabled', true);
												}}
												type="button"
											>
												<MemberAvatar
													displayName={profile.displayName}
													enabled
													preset={preset.id}
													size="medium"
													tooltip={false}
												/>
											</button>
										))}
										{profile.avatarDataUrls.map((dataUrl, index) => (
											<span
												className="avatar-custom-option"
												key={dataUrl}
												title="Delete avatar"
											>
												<button
													aria-label={`Use custom avatar ${index + 1}`}
													className={`avatar-choice-button avatar-uploaded-option ${
														profile.avatarKind === 'uploaded' &&
														profile.avatarDataUrl === dataUrl
															? 'selected'
															: ''
													}`}
													onClick={() => {
														changeProfile('avatarKind', 'uploaded');
														changeProfile('avatarDataUrl', dataUrl);
														changeProfile('avatarEnabled', true);
													}}
													type="button"
												>
													<MemberAvatar
														dataUrl={dataUrl}
														displayName={profile.displayName}
														enabled
														preset={profile.avatarPreset}
														size="medium"
														tooltip={false}
													/>
												</button>
												<button
													aria-label="Delete avatar"
													className="avatar-delete-button"
													onClick={() => deleteCustomAvatar(dataUrl)}
													type="button"
												>
													<svg aria-hidden="true" viewBox="0 0 12 12">
														<path d="M2 2l8 8M10 2 2 10" />
													</svg>
												</button>
											</span>
										))}
									</div>
								</div>
								<label className="avatar-upload-control">
									<span aria-hidden="true" className="avatar-upload-icon">
										↑
									</span>
									<span>
										<strong>Upload your own</strong>
										<small>PNG, JPG or WebP up to 5 MB</small>
									</span>
									<input
										accept="image/png,image/jpeg,image/webp"
										hidden
										multiple
										onChange={(event) => void chooseAvatar(event)}
										type="file"
									/>
								</label>
							</div>
						</div>

						{profile.canManageAccount && profile.role !== 'owner' && (
							<div className="settings-grid">
								<label>
									<span>Role</span>
									<select
										disabled={membership.role === 'supervisor'}
										onChange={(event) =>
											changeProfile(
												'role',
												event.currentTarget
													.value as TenantMemberProfile['role'],
											)
										}
										value={profile.role}
									>
										<option value="agent">Agent</option>
										<option value="supervisor">Supervisor</option>
										<option value="admin">Administrator</option>
									</select>
								</label>
								<label>
									<span>Account access</span>
									<select
										disabled={profile.status === 'invited'}
										onChange={(event) =>
											changeProfile(
												'status',
												event.currentTarget
													.value as TenantMemberProfile['status'],
											)
										}
										value={profile.status}
									>
										{profile.status === 'invited' && (
											<option value="invited">Invitation pending</option>
										)}
										<option value="active">Active</option>
										<option value="suspended">Suspended</option>
									</select>
								</label>
							</div>
						)}
					</section>

					{profile.memberType === 'ai_agent' && (
						<section
							aria-labelledby="ai-agent-instructions-heading"
							className="settings-card ai-agent-instructions-panel"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id="ai-agent-instructions-heading">
									Persona and rules
								</h4>
								<p>
									Define how this agent should speak and behave. Its current
									display name is always supplied separately to the AI. Changes
									stay in Draft until an owner or administrator publishes them
									under Settings → AI.
								</p>
							</div>
							<label>
								<span>Instructions</span>
								<textarea
									disabled={!profile.canManageAiInstructions}
									maxLength={12_000}
									onChange={(event) =>
										changeProfile('aiInstructions', event.currentTarget.value)
									}
									placeholder="For example: Be warm and concise. Never offer a refund without handing the chat to a person."
									rows={10}
									value={profile.aiInstructions}
								/>
								<small>
									These instructions cannot override the requirement to answer
									from this agent’s knowledge.
								</small>
							</label>
						</section>
					)}

					{profile.memberType === 'ai_agent' && (
						<section className="settings-card ai-agent-knowledge-access-panel">
							<div className="integration-panel-heading">
								<h4 className="panel-title">Knowledge access</h4>
								<p>
									Reusable company knowledge is assigned to this agent directly or
									through a department in Settings → AI.
								</p>
							</div>
						</section>
					)}

					{profile.memberType === 'human' && profile.messageEnhanceAvailable && (
						<section
							aria-labelledby="message-enhance-settings-heading"
							className="settings-card message-enhance-user-settings"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id="message-enhance-settings-heading">
									Message Enhance
								</h4>
								<p>
									Choose how much AI assistance is applied while you compose
									replies.
								</p>
							</div>
							<label className="owner-settings-field message-enhance-level-field">
								<span>Level</span>
								<select
									onChange={(event) =>
										changeProfile(
											'messageEnhanceLevel',
											event.currentTarget
												.value as TenantMemberProfile['messageEnhanceLevel'],
										)
									}
									value={profile.messageEnhanceLevel}
								>
									<option value="off">Off (Default)</option>
									<option value="basic">Basic</option>
									<option value="advanced">Advanced</option>
								</select>
								<small>
									Basic corrects spelling and grammar. Advanced may also rewrite
									sentences.
								</small>
							</label>
						</section>
					)}

					{isSelf && profile.memberType === 'human' && profile.aiCopilotAvailable && (
						<section
							aria-labelledby="ai-copilot-settings-heading"
							className="settings-card ai-copilot-user-settings"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id="ai-copilot-settings-heading">
									AI Copilot
								</h4>
								<p>
									Choose whether Copilot can offer draft-only assistance while you
									work. It never sends a reply for you.
								</p>
							</div>
							<label className="switch-row compact-switch">
								<div className="settings-option-copy">
									<span className="settings-option-title">Enable AI Copilot</span>
									<span className="settings-option-description">
										Use company-approved AI assistance in conversations.
									</span>
								</div>
								<input
									checked={profile.aiCopilotEnabled}
									onChange={(event) =>
										changeProfile(
											'aiCopilotEnabled',
											event.currentTarget.checked,
										)
									}
									type="checkbox"
								/>
							</label>
						</section>
					)}

					{isSelf && (
						<section
							aria-labelledby="notification-settings-heading"
							className="settings-card notification-settings"
						>
							<div className="integration-panel-heading">
								<h4 className="panel-title" id="notification-settings-heading">
									Notifications
								</h4>
								<p>Choose which events notify you and how alerts are delivered.</p>
							</div>
							<div className="notification-preference-list">
								<label className="switch-row compact-switch">
									<div className="settings-option-copy">
										<span className="settings-option-title">
											New customers waiting
										</span>
										<span className="settings-option-description">
											Alert you when a new chat arrives.
										</span>
									</div>
									<input
										checked={profile.newChatNotificationsEnabled}
										onChange={(event) =>
											changeProfile(
												'newChatNotificationsEnabled',
												event.currentTarget.checked,
											)
										}
										type="checkbox"
									/>
								</label>
								<label className="switch-row compact-switch">
									<div className="settings-option-copy">
										<span className="settings-option-title">
											New messages received
										</span>
										<span className="settings-option-description">
											Alert when a visitor replies outside the open chat.
										</span>
									</div>
									<input
										checked={profile.newMessageNotificationsEnabled}
										onChange={(event) =>
											changeProfile(
												'newMessageNotificationsEnabled',
												event.currentTarget.checked,
											)
										}
										type="checkbox"
									/>
								</label>
								<label className="notification-setting-row notification-sound-setting">
									<div className="settings-option-copy">
										<span className="settings-option-title">Sound alert</span>
										<span className="settings-option-description">
											Choose a sound for enabled events, or select None.
										</span>
									</div>
									<select
										aria-label="Sound alert"
										onChange={(event) =>
											changeNotificationSound(event.currentTarget.value)
										}
										value={notificationSoundChoice(
											profile.soundNotificationsEnabled,
											profile.notificationSound,
										)}
									>
										<option value="none">None</option>
										<option value="chime">Chime</option>
										<option value="bell">Bell</option>
										<option value="pop">Pop</option>
									</select>
								</label>
								<div className="notification-setting-row notification-method-setting">
									<div className="settings-option-copy">
										<span className="settings-option-title">Alert method</span>
										<span className="settings-option-description">
											Choose either a browser or system alert.
										</span>
									</div>
									<div className="notification-method-actions">
										<div
											aria-label="Alert method"
											className="segmented-control notification-method-toggle"
											role="group"
										>
											<button
												aria-pressed={!profile.browserNotificationsEnabled}
												onClick={() =>
													void toggleSystemNotifications(false)
												}
												type="button"
											>
												Browser
											</button>
											<button
												aria-pressed={profile.browserNotificationsEnabled}
												onClick={() => void toggleSystemNotifications(true)}
												type="button"
											>
												System
											</button>
										</div>
										<button
											className="secondary-button compact-button"
											onClick={() => void testSelectedNotifications()}
											type="button"
										>
											Test
										</button>
									</div>
								</div>
								{notificationTestResult && (
									<div className={`alert ${notificationTestResult.kind}`}>
										{notificationTestResult.message}
									</div>
								)}
							</div>
						</section>
					)}
					<button className="primary-button" disabled={busy}>
						{busy ? 'Saving…' : 'Save user settings'}
					</button>
					{profile.memberType === 'human' &&
						(isSelf || profile.canCompleteInvitation) && (
							<section className="settings-card password-settings-panel">
								<div className="integration-panel-heading">
									<h4 className="panel-title">
										{isSelf ? 'Change password' : 'Complete invitation'}
									</h4>
									<p>
										{isSelf
											? 'Choose a new password for your account. Other signed-in sessions will be logged out.'
											: 'Set an initial password to activate this account when the invitation email was not received.'}
									</p>
								</div>
								{passwordError && (
									<div className="alert error">{passwordError}</div>
								)}
								<div className="settings-grid">
									{isSelf && (
										<label>
											<span>Current password</span>
											<input
												autoComplete="current-password"
												maxLength={128}
												onChange={(event) =>
													setCurrentPassword(event.currentTarget.value)
												}
												type="password"
												value={currentPassword}
											/>
										</label>
									)}
									<label>
										<span>New password</span>
										<input
											autoComplete="new-password"
											maxLength={128}
											minLength={12}
											onChange={(event) =>
												setNewPassword(event.currentTarget.value)
											}
											type="password"
											value={newPassword}
										/>
										<small>Use at least 12 characters.</small>
									</label>
									<label>
										<span>Confirm new password</span>
										<input
											autoComplete="new-password"
											maxLength={128}
											minLength={12}
											onChange={(event) =>
												setConfirmPassword(event.currentTarget.value)
											}
											type="password"
											value={confirmPassword}
										/>
									</label>
								</div>
								<button
									className="primary-button"
									disabled={
										passwordBusy ||
										!newPassword ||
										!confirmPassword ||
										(isSelf && !currentPassword)
									}
									onClick={() => void savePassword()}
									type="button"
								>
									{passwordBusy
										? 'Saving…'
										: isSelf
											? 'Update password'
											: 'Set password and activate user'}
								</button>
							</section>
						)}
					{!isSelf && profile.canManageAccount && (
						<section className="settings-card danger-zone">
							<div>
								<strong>Delete user</strong>
								<p>
									Remove this user’s company access and uploaded avatar.
									Historical chat attribution is retained.
								</p>
							</div>
							<button
								className="danger-button"
								disabled={busy}
								onClick={() => setConfirmDelete(true)}
								type="button"
							>
								Delete user
							</button>
						</section>
					)}
				</TenantSettingsFormLayout>
			)}
			{confirmDelete && profile && (
				<div className="dialog-backdrop" role="presentation">
					<section aria-modal="true" className="dialog-card" role="dialog">
						<h3>Delete {profile.displayName}?</h3>
						<p>
							They will immediately lose access to this company. Open chats assigned
							to them will return to the queue.
						</p>
						<div className="dialog-actions">
							<button
								className="secondary-button"
								disabled={busy}
								onClick={() => setConfirmDelete(false)}
								type="button"
							>
								Cancel
							</button>
							<button
								className="danger-button"
								disabled={busy}
								onClick={() => void removeMember()}
								type="button"
							>
								{busy ? 'Deleting…' : 'Delete user'}
							</button>
						</div>
					</section>
				</div>
			)}
			<NotificationToastRegion dismiss={dismissTestToast} toasts={testToasts} />
		</div>
	);
}
